import React, { useEffect, useState, useCallback } from 'react';
import { X, Loader2, Search } from 'lucide-react';
import { API_ENDPOINTS } from '../../api/endpoints';
import api from '../../api/axios';
import { format } from 'date-fns';

interface LeaveRequest {
  Id: number;
  ApplicantRefId: number;
  ApplicantType: number;
  ApplicantName?: string;
  DriverName?: string;
  FromDate: string;
  ToDate: string;
  LeaveTypeName: string;
  Reason: string;
  StatusName: string;
  TotalDays: number;
}

/** LeaveRequestMaster.ApplicantType: 1 = employee, 2 = driver. */
const DRIVER_APPLICANT = 2;

interface DriverLeaveModalProps {
  onClose: () => void;
}

export function DriverLeaveModal({ onClose }: DriverLeaveModalProps) {
  const [leaves, setLeaves] = useState<LeaveRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Initialize dates to today
  const todayStr = format(new Date(), 'yyyy-MM-dd');
  const [fromDate, setFromDate] = useState(todayStr);
  const [toDate, setToDate] = useState(todayStr);

  const fetchLeaves = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Append time to match backend's LocalDateTime pattern: yyyy-MM-dd'T'HH:mm:ss
      const formattedFromDate = fromDate ? `${fromDate}T00:00:00` : null;
      const formattedToDate = toDate ? `${toDate}T23:59:59` : null;

      // The server keeps the search to the signed-in user's company; this view lists drivers only.
      const response = await api.post(API_ENDPOINTS.LEAVE.SEARCH, {
        ApplicantType: DRIVER_APPLICANT,
        FromDate: formattedFromDate,
        ToDate: formattedToDate
      });
      
      if (response.data?.IsSuccess && Array.isArray(response.data.Data1)) {
        setLeaves(response.data.Data1);
      } else {
        setLeaves([]);
      }
    } catch (err: any) {
      console.error("Error fetching active leaves", err);
      setError("Failed to load leave requests");
      setLeaves([]);
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate]);

  // Initial fetch on mount
  useEffect(() => {
    fetchLeaves();
  }, [fetchLeaves]);

  const formatDateLabel = (dateString: string) => {
    if (!dateString) return '-';
    try {
      return format(new Date(dateString), 'dd MMM yyyy');
    } catch {
      return dateString;
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="flex w-full max-w-4xl flex-col max-h-[90vh] rounded-xl bg-white shadow-2xl overflow-hidden">
        
        {/* Header */}
        <div className="flex items-center justify-between border-b px-6 py-4 bg-slate-50">
          <h2 className="text-lg font-bold text-slate-800">Driver Leave Detail</h2>
          <button
            onClick={onClose}
            className="rounded-lg p-2 text-slate-400 hover:bg-slate-200 hover:text-slate-700 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Filters */}
        <div className="flex items-end gap-4 border-b px-6 py-4 bg-white">
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-semibold text-slate-600">From Date</label>
            <input 
              type="date" 
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="rounded-md border border-slate-300 px-3 py-1.5 text-sm outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-semibold text-slate-600">To Date</label>
            <input 
              type="date" 
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="rounded-md border border-slate-300 px-3 py-1.5 text-sm outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
            />
          </div>
          <button
            onClick={fetchLeaves}
            disabled={loading}
            className="flex h-[34px] items-center gap-2 rounded-md bg-blue-600 px-4 py-1.5 text-sm font-semibold text-white transition-colors hover:bg-blue-700 disabled:opacity-70"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
            Search
          </button>
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-auto p-6 bg-slate-50/50">
          {loading ? (
            <div className="flex h-40 flex-col items-center justify-center space-y-4">
              <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
              <p className="text-sm font-medium text-slate-500">Searching leave requests...</p>
            </div>
          ) : error ? (
            <div className="flex h-40 items-center justify-center text-red-500 font-medium">
              {error}
            </div>
          ) : leaves.length === 0 ? (
            <div className="flex h-40 flex-col items-center justify-center text-slate-500">
              <p className="text-base font-medium">No leave requests found for this date range.</p>
            </div>
          ) : (
            <div className="rounded-lg border border-slate-200 bg-white shadow-sm overflow-hidden">
              <table className="w-full text-left text-sm text-slate-600">
                <thead className="bg-slate-100 text-xs font-semibold uppercase text-slate-700">
                  <tr>
                    <th className="px-4 py-3">Driver Name</th>
                    <th className="px-4 py-3">Type</th>
                    <th className="px-4 py-3">From Date</th>
                    <th className="px-4 py-3">To Date</th>
                    <th className="px-4 py-3">Days</th>
                    <th className="px-4 py-3">Reason</th>
                    <th className="px-4 py-3">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {leaves.map((leave) => (
                    <tr key={leave.Id} className="hover:bg-blue-50/50 transition-colors">
                      <td className="px-4 py-3 font-bold text-slate-800">
                        {leave.ApplicantName || leave.DriverName || leave.ApplicantRefId}
                      </td>
                      <td className="px-4 py-3">
                        <span className="inline-flex rounded-full bg-blue-100 px-2.5 py-0.5 text-[11px] font-semibold text-blue-700">
                          {leave.LeaveTypeName}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap font-medium">{formatDateLabel(leave.FromDate)}</td>
                      <td className="px-4 py-3 whitespace-nowrap font-medium">{formatDateLabel(leave.ToDate)}</td>
                      <td className="px-4 py-3 font-semibold text-center">{leave.TotalDays}</td>
                      <td className="px-4 py-3 max-w-[200px] truncate" title={leave.Reason}>
                        {leave.Reason || '-'}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex rounded-full px-2.5 py-0.5 text-[11px] font-bold uppercase ${
                            leave.StatusName?.toUpperCase() === 'APPROVED'
                              ? 'bg-emerald-100 text-emerald-700'
                              : 'bg-amber-100 text-amber-700'
                          }`}
                        >
                          {leave.StatusName}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div className="flex items-center justify-end border-t px-6 py-4 bg-white">
          <button
            onClick={onClose}
            className="rounded-lg bg-slate-800 px-5 py-2 text-sm font-semibold text-white hover:bg-slate-700 transition-colors active:scale-95"
          >
            Close
          </button>
        </div>
        
      </div>
    </div>
  );
}
